import React, { useLayoutEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import {useTranslations} from "use-intl";

import { ParamsType } from '@/modules/Product/components/Main/components/Tabs/Tabs';
import { colors } from '@/constants/theme';
import Link from '@/components/Link';
import { ReactComponent as Dlina } from '@/assets/svg/sofa_length.svg';
import { ReactComponent as Glubina } from '@/assets/svg/sofa_width.svg';
import { ReactComponent as Vysota } from '@/assets/svg/sofa_heigth.svg';
import { ReactComponent as DlinaMattress } from '@/assets/svg/mattress_length.svg';
import { ReactComponent as ShyrinaMattress } from '@/assets/svg/mattress_width.svg';
import { ProductParamsValueType, ProductSize } from '@/typings/model';
import { getAliasString } from '@/utils/common';
import { useTypedSelector } from '@/store/store';
import { selectProductResource } from '@/store/reducers/pages/product/product';

enum SizeMejanizm {
  SIZE = 'size',
  SLEEPING_SIZE = 'sleeping-size',
  DLINA = 'DLINA',
  VYSOTA = 'VYSOTA',
  GLUBINA = 'GLUBINA',
  DLINA_SPALNOGO_MESTA = 'DLINA-SPALNOGO-MESTA',
  SHIRINA_SPALNOGO_MESTA = 'SHIRINA-SPALNOGO-MESTA',
  MEHANIZMY = 'MEHANIZM-TRANSFORMACII',
  KOMPLEKTACIYA = 'KOMPLEKTACIYA',
}

function getIconSize(type: string) {
  switch (type) {
    case SizeMejanizm.DLINA:
      return <Dlina />;
    case SizeMejanizm.VYSOTA:
      return <Vysota />;
    case SizeMejanizm.GLUBINA:
      return <Glubina />;
    case SizeMejanizm.DLINA_SPALNOGO_MESTA:
      return <DlinaMattress />;
    case SizeMejanizm.SHIRINA_SPALNOGO_MESTA:
      return <ShyrinaMattress />;
    default:
      return null;
  }
}

type Props = {
  data: ParamsType[];
  setHeight?: (height: number) => void;
};

function getSizeMehanizmData(
  data: ParamsType[],
  size: boolean,
  matress: boolean
) {
  const t = useTranslations();

  return (
    <>
      {data.map(({ label, value, to, category }, index) => {
        if (category === SizeMejanizm.MEHANIZMY) {
          return (
            <MehanizmWrapper key={index}>
              <Label>{label}</Label>
              <Note>{value}</Note>
              <Link to={to}>
                <MehanizmButton>{t('mechanism_more')}</MehanizmButton>
              </Link>
            </MehanizmWrapper>
          );
        }
      })}
      {size ? (
        <SizeWrapper>
          <Label>{'params.param_3'}</Label>
          <SizeContent>
            {data.map(({ type, label, value, to, category }, index) => {
              if (category === SizeMejanizm.SIZE) {
                if (
                  type === SizeMejanizm.DLINA ||
                  type === SizeMejanizm.VYSOTA ||
                  type === SizeMejanizm.GLUBINA
                ) {
                  return (
                    <Param isFlex className="size-wrapper__param" key={index}>
                      {getIconSize(type)}
                      <DescriptionWrapper>
                        <Label isGray size={11}>
                          {label}
                        </Label>
                        <Note>{value}</Note>
                      </DescriptionWrapper>
                    </Param>
                  );
                }
              }
            })}
          </SizeContent>
        </SizeWrapper>
      ) : null}
      {matress ? (
        <SizeWrapper>
          <Label>{'params.param_2'}</Label>
          <SizeContent>
            {data.map(({ type, label, value, to, category }, index) => {
              if (category === SizeMejanizm.SLEEPING_SIZE) {
                if (
                  type === SizeMejanizm.DLINA_SPALNOGO_MESTA ||
                  type === SizeMejanizm.SHIRINA_SPALNOGO_MESTA
                ) {
                  return (
                    <Param isFlex key={index}>
                      {getIconSize(type)}
                      <DescriptionWrapper>
                        <Label isGray size={11}>
                          {label}
                        </Label>
                        <Note>{value}</Note>
                      </DescriptionWrapper>
                    </Param>
                  );
                }
              }
            })}
          </SizeContent>
        </SizeWrapper>
      ) : null}
    </>
  );
}

function getRestParams(data: ParamsType[]) {
  return (
    <>
      {data.map(({ type, label, value, to, category, multiple }, index) => {
        if (type === 'SELECT' && category !== SizeMejanizm.MEHANIZMY) {
          return (
            <Param key={index}>
              <Label isGray>{label}</Label>
              {multiple && Array.isArray(value) ? (
                value.map((item, index) => (
                  <Note key={index}>{item.label}</Note>
                ))
              ) : (
                <Note key={index}>
                  {(value as ProductParamsValueType)?.label ?? value}
                </Note>
              )}
            </Param>
          );
        }
        if (type === 'NUMBER' && category !== SizeMejanizm.MEHANIZMY) {
          return (
            <Param key={index}>
              <Label isGray>{label}</Label>
              <Note>{value}</Note>
            </Param>
          );
        }
        if (type === 'TRUE_FALSE' && category !== SizeMejanizm.MEHANIZMY) {
          return (
            <Param key={index}>
              <Label isGray>{label}</Label>
              <Note>{value}</Note>
            </Param>
          );
        }

        if (type === 'STRING') {
          return (
            <Param key={index}>
              <Label isGray>{label}</Label>
              <Note>{value}</Note>
            </Param>
          );
        }
      })}
    </>
  );
}

function Params({ data, setHeight }: Props) {
  const router = useRouter();
  const alias = getAliasString(router.query.alias);
  const { data: product } = useTypedSelector((state) =>
    selectProductResource(state, alias)
  );

  const hasSize = !!(product?.size as ProductSize[]).length;
  const hasMatressSize = !!(product?.matressSize as ProductSize[]).length;

  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (ref.current) {
      setHeight?.(ref.current.scrollHeight);
    }
  });

  return (
    <Component ref={ref}>
      {getSizeMehanizmData(data, hasSize, hasMatressSize)}
      {getRestParams(data)}
    </Component>
  );
}

export default Params;

const Component = styled.div`
  max-width: 480px;
  padding-bottom: 20px;

  & > *:first-child {
    margin-top: 0;
  }
`;

const MehanizmWrapper = styled.div``;
const SizeWrapper = styled.div`
  margin-top: 20px;
`;
const SizeContent = styled.div`
  margin-top: 5px;

  display: flex;
  justify-content: space-between;
  .size-wrapper__param {
    margin-top: 0;
  }

  & > div {
    margin-top: 0;
  }
`;
const MehanizmButton = styled.button`
  font-style: normal;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  color: ${colors.primary};
`;

const Param = styled.div<{ isFlex?: boolean }>`
  margin-top: 20px;
  flex: 1;
  ${({ isFlex }) =>
    isFlex
      ? css`
          display: flex;
          align-items: center;
        `
      : null}
`;

const DescriptionWrapper = styled.div`
  margin-left: 5px;
`;
const Label = styled.div<{ isGray?: boolean; size?: number }>`
  font-style: normal;
  line-height: 18px;
  ${({ isGray }) =>
    isGray
      ? css`
          color: ${colors.gray400};
        `
      : css`
          color: ${colors.black};
        `}
  ${({ size }) =>
    size
      ? css`
          font-size: ${size}px;
          font-weight: normal;
        `
      : css`
          font-size: 13px;
          font-weight: 600;
        `}
`;
const Note = styled.div`
  margin-top: 2px;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 18px;
`;
